import { useActiveAddress } from "@arweave-wallet-kit/react"
import { useMutation, useQuery, UseMutationResult } from "@tanstack/react-query"
import React, { createContext, useContext, useEffect, useState, ReactNode } from "react"

import { getSimpleAgent, deploySimpleAgent, getSimpleAgentInfo } from "../ao/agent"
import { useAo } from "../ao/ao"

interface SimpleAgentInfo {
  [key: string]: string
}

// Define the context
interface SimpleAgentContextValue {
  agentProcess: string | undefined
  agent: SimpleAgentInfo | undefined
  loading: boolean
  deployAgent: UseMutationResult<string, Error, void, unknown>
}

const SimpleAgentContext = createContext<SimpleAgentContextValue | null>(null)

// Create a provider component
export const SimpleAgentProvider = ({ children }: { children: ReactNode }) => {
  const activeAddress = useActiveAddress()
  const ao = useAo()

  const [agentProcess, setAgentProcess] = useState<string | undefined>(undefined)
  const [agent, setAgent] = useState<SimpleAgentInfo | undefined>()
  const [loading, setLoading] = useState(true)

  const agentProcessQuery = useQuery({
    queryKey: ["Get-Simple-Agent", activeAddress],
    queryFn: async () => {
      return await getSimpleAgent(activeAddress as string, ao)
    },
    enabled: !!ao && !!activeAddress,
  })

  useEffect(() => {
    console.log("agentProcessQuery", agentProcessQuery)
    if (agentProcessQuery.data !== agentProcess) {
      setAgentProcess(agentProcessQuery.data)
      setLoading(false)
    }
  }, [agentProcessQuery])

  const agentInfoQuery = useQuery({
    queryKey: ["Get-Simple-Agent-Info", agentProcess, ao],
    queryFn: async () => {
      return await getSimpleAgentInfo(agentProcess as string, ao)
    },
    enabled: !!ao && !!agentProcess,
  })

  useEffect(() => {
    if (agentInfoQuery.data !== agent) {
      setAgent(agentInfoQuery.data)
      setLoading(false)
    }
  }, [agentInfoQuery])

  const deployAgent = useMutation({
    mutationKey: ["deployHopperAgent"],
    mutationFn: async () => {
      const result = await deploySimpleAgent(ao)

      return result
    },
  })

  return (
    <SimpleAgentContext.Provider value={{ agent, agentProcess, loading, deployAgent }}>
      {children}
    </SimpleAgentContext.Provider>
  )
}

// Create a custom hook to use the SimpleAgentContext
export const useSimpleAgent = () => {
  return useContext(SimpleAgentContext)
}
